import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import SimpleTable, { type SimpleTableColumn } from './SimpleTable';

// Generic product interface for reusability
export interface ProductoBase {
  id: number;
  idArticulo: number;
  codigoArticulo: string;
  descripcion: string;
  especieNombreComun?: string;
  coeficiente: number;
  pesoEmpaqueInterior: number;
  cantidad: number;
  total: number;
  calibre?: string;
  presentacion?: string;
  idPresentacion?: number;
}

// Column configuration for additional custom columns
export interface CustomColumn<T> {
  id: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: number;
  render: (value: any, row: T) => React.ReactNode;
}

interface ProductosPorPresentacionTableProps<T extends ProductoBase> {
  productos: T[];
  onCantidadChange?: (id: number, cantidad: number) => void;
  onRemoveProducto?: (id: number) => void;
  onAddProducto?: () => void;
  onEditPresentacion?: (idPresentacion: number, productos: T[]) => void;
  addButtonText?: string;
  emptyMessage?: string;
  emptySubMessage?: string;
  showPesoEmpaque?: boolean;
  showCalibre?: boolean;
  showPresentacionColumn?: boolean;
  customColumns?: CustomColumn<T>[];
  customColumnsPosition?: 'before-cantidad' | 'after-cantidad' | 'after-descripcion';
  readOnly?: boolean;
  // Para calcular rendimiento (peso del sublote/materia prima)
  pesoMateriaPrima?: number;
  showRendimiento?: boolean;
}

function ProductosPorPresentacionTable<T extends ProductoBase>({
  productos,
  onCantidadChange,
  onRemoveProducto,
  onAddProducto,
  onEditPresentacion,
  addButtonText = 'Agregar Producto',
  emptyMessage = 'No hay productos agregados',
  emptySubMessage,
  showPesoEmpaque = true,
  showCalibre = true,
  showPresentacionColumn = true,
  customColumns = [],
  customColumnsPosition = 'before-cantidad',
  readOnly = false,
  pesoMateriaPrima,
  showRendimiento = false,
}: ProductosPorPresentacionTableProps<T>) {
  // Group products by presentation first to determine initial expanded state
  const productosPorPresentacion = productos.reduce((grupos, producto) => {
    const presentacion = producto.presentacion || 'Sin Presentación';
    if (!grupos[presentacion]) {
      grupos[presentacion] = [];
    }
    grupos[presentacion].push(producto);
    return grupos;
  }, {} as Record<string, T[]>);

  // Initialize with first presentation expanded
  const firstPresentacion = Object.keys(productosPorPresentacion)[0] || false;
  const [expandedPresentacion, setExpandedPresentacion] = useState<string | false>(firstPresentacion);

  // Update expanded presentation when products change
  useEffect(() => {
    const currentFirstPresentacion = Object.keys(productosPorPresentacion)[0] || false;
    if (currentFirstPresentacion && currentFirstPresentacion !== expandedPresentacion) {
      setExpandedPresentacion(currentFirstPresentacion);
    }
  }, [productos.length]); // Re-run when number of products changes

  const handleCantidadChange = (id: number, value: string) => {
    if (!onCantidadChange) return;
    const numericValue = parseFloat(value) || 0;
    onCantidadChange(id, numericValue);
  };

  const handleAccordionChange = (presentacion: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPresentacion(isExpanded ? presentacion : false);
  };

  // Calculate totals
  const totalCantidad = productos.reduce((sum, producto) => sum + (producto.cantidad || 0), 0);
  const totalPeso = productos.reduce((sum, producto) => sum + (producto.total || 0), 0);

  // Build columns dynamically
  const buildColumns = (): SimpleTableColumn[] => {
    const columns: SimpleTableColumn[] = [
      {
        id: 'codigoArticulo',
        label: 'Código',
        align: 'left',
        width: 100,
      },
      {
        id: 'descripcion',
        label: 'Descripción',
        align: 'left',
      },
    ];

    // Add presentation column if enabled
    if (showPresentacionColumn) {
      columns.push({
        id: 'presentacion',
        label: 'Presentación',
        align: 'left',
        width: 150,
        render: (_value: any, row: T) => (
          <Chip label={row.presentacion || 'N/A'} size="small" variant="outlined" />
        ),
      });
    }

    // Add calibre column if enabled
    if (showCalibre) {
      columns.push({
        id: 'calibre',
        label: 'Calibre',
        align: 'left',
        width: 100,
        render: (_value: any, row: T) => (
          <Chip label={row.calibre || 'N/A'} size="small" color="info" variant="outlined" />
        ),
      });
    }

    // Add custom columns after descripcion
    if (customColumnsPosition === 'after-descripcion') {
      customColumns.forEach(col => {
        columns.push({
          id: col.id,
          label: col.label,
          align: col.align || 'left',
          width: col.width,
          render: col.render as (value: any, row: ProductoBase) => React.ReactNode,
        });
      });
    }

    // Add peso empaque column if enabled
    if (showPesoEmpaque) {
      columns.push({
        id: 'pesoEmpaqueInterior',
        label: 'Peso Emp. (kg)',
        align: 'right',
        width: 110,
        render: (_value: any, row: T) => (
          <Box sx={{ textAlign: 'right' }}>
            {(row.pesoEmpaqueInterior || 0).toFixed(2)}
          </Box>
        ),
      });
    }

    // Add custom columns before cantidad
    if (customColumnsPosition === 'before-cantidad') {
      customColumns.forEach(col => {
        columns.push({
          id: col.id,
          label: col.label,
          align: col.align || 'left',
          width: col.width,
          render: col.render as (value: any, row: ProductoBase) => React.ReactNode,
        });
      });
    }

    // Cantidad column
    columns.push({
      id: 'cantidad',
      label: 'Cantidad',
      align: 'right',
      width: 100,
      render: (_value: any, row: T) => (
        readOnly ? (
          <Box sx={{ textAlign: 'right' }}>
            {row.cantidad}
          </Box>
        ) : (
          <TextField
            size="small"
            type="number"
            value={row.cantidad}
            onChange={(e) => handleCantidadChange(row.id, e.target.value)}
            slotProps={{
              input: {
                style: { textAlign: 'right', width: '80px' }
              },
              htmlInput: {
                min: 0,
                step: 1
              }
            }}
            variant="outlined"
          />
        )
      ),
    });

    // Add custom columns after cantidad
    if (customColumnsPosition === 'after-cantidad') {
      customColumns.forEach(col => {
        columns.push({
          id: col.id,
          label: col.label,
          align: col.align || 'left',
          width: col.width,
          render: col.render as (value: any, row: ProductoBase) => React.ReactNode,
        });
      });
    }

    // Total column
    columns.push({
      id: 'total',
      label: 'Total (kg)',
      align: 'right',
      width: 100,
      render: (_value: any, row: T) => (
        <Box sx={{ textAlign: 'right', fontWeight: 500 }}>
          {(row.total || 0).toFixed(2)}
        </Box>
      ),
    });

    // Actions column (if not readOnly and onRemoveProducto is provided)
    if (!readOnly && onRemoveProducto) {
      columns.push({
        id: 'actions',
        label: 'Acciones',
        align: 'center',
        width: 80,
        render: (_value: any, row: T) => (
          <Tooltip title="Eliminar producto">
            <IconButton
              size="small"
              onClick={() => onRemoveProducto(row.id)}
              sx={{
                color: 'error.main',
                '&:hover': {
                  backgroundColor: 'error.light',
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      });
    }

    return columns;
  };

  const productColumns = buildColumns();

  return (
    <Box>
      {/* Add button */}
      {!readOnly && onAddProducto && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddProducto}
            size="small"
          >
            {addButtonText}
          </Button>
        </Box>
      )}

      {/* Empty state */}
      {productos.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
          {emptySubMessage && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {emptySubMessage}
            </Typography>
          )}
        </Box>
      )}

      {/* Accordions by presentation - sorted: CAJAS, BOLSAS, SALDOS */}
      {Object.entries(productosPorPresentacion)
        .sort(([a], [b]) => {
          const getOrder = (pres: string): number => {
            const presUpper = pres.toUpperCase();
            if (presUpper.includes('CAJA')) return 1;
            if (presUpper.includes('BOLSA')) return 2;
            if (presUpper.includes('SALDO')) return 3;
            return 4;
          };
          const orderDiff = getOrder(a) - getOrder(b);
          return orderDiff !== 0 ? orderDiff : a.localeCompare(b);
        })
        .map(([presentacion, productosGrupo]) => {
        const totalCantidadGrupo = productosGrupo.reduce((sum, p) => sum + (p.cantidad || 0), 0);
        const totalTotalGrupo = productosGrupo.reduce((sum, p) => sum + (p.total || 0), 0);

        return (
          <Accordion
            key={presentacion}
            expanded={expandedPresentacion === presentacion}
            onChange={handleAccordionChange(presentacion)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: 'grey.50',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {presentacion}
                </Typography>
                <Chip
                  label={`${productosGrupo.length} productos`}
                  size="small"
                  color="primary"
                />
                <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Chip
                    label={`Cantidad: ${totalCantidadGrupo}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Total: ${totalTotalGrupo.toFixed(2)} kg`}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                  {!readOnly && onEditPresentacion && productosGrupo[0]?.idPresentacion && (
                    <Tooltip title="Editar presentación">
                      <Box
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditPresentacion(productosGrupo[0].idPresentacion!, productosGrupo);
                        }}
                        sx={{
                          ml: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          color: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <SimpleTable
                columns={productColumns}
                rows={productosGrupo}
                emptyMessage="No hay productos en este grupo"
                getRowId={(row) => row.id}
                hover={true}
              />

              {/* Group totals row */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  backgroundColor: 'primary.light',
                  borderRadius: 1,
                  p: 1.5,
                  mt: 1,
                  gap: 4,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  SUBTOTALES:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  Cantidad: {totalCantidadGrupo}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  Total: {totalTotalGrupo.toFixed(2)} kg
                </Typography>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Grand totals */}
      {productos.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            backgroundColor: 'secondary.light',
            borderRadius: 1,
            p: 2,
            mt: 2,
            gap: 4,
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            TOTALES GENERALES:
          </Typography>
          <Chip
            label={`Cantidad: ${totalCantidad}`}
            color="primary"
            size="medium"
          />
          <Chip
            label={`Total: ${totalPeso.toFixed(2)} kg`}
            color="secondary"
            size="medium"
          />
          {showRendimiento && pesoMateriaPrima && pesoMateriaPrima > 0 && (
            <Chip
              label={`Rendimiento: ${(totalPeso / pesoMateriaPrima).toFixed(2)}`}
              sx={{
                backgroundColor: '#ffeb3b',
                color: '#000',
                fontWeight: 700
              }}
              size="medium"
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export default ProductosPorPresentacionTable;
